/* product-description.js - the "Full details" unfold, in place, no jump.

   The anchor in blocks/product-description.liquid is the floor: without this
   file a click still opens the copy through `:target`. What the anchor cannot
   do is open WITHOUT SCROLLING, because a fragment navigation always scrolls to
   its target, and that scroll is what the founder reported (measured numbers in
   the block's doc comment). So the click is taken here and four things happen:

   1. NO SCROLL, NO HISTORY. preventDefault() keeps the address bar and the
      history untouched: Back leaves the page instead of folding the copy, and
      assets/header.js never sees a jump it would read as a scroll gesture.
   2. THE BOX GROWS FROM WHERE IT IS. The first three lines stay exactly under
      the shopper's eyes; only what is below the copy moves, and it moves over
      the unfold's duration instead of in one frame.
   3. FOCUS FOLLOWS THE COPY. The link retires (display:none) the moment the
      copy is open, and a focus left on a removed control falls back to the
      document. The copy takes it instead, without scrolling, so a screen
      reader stays in the description and Tab carries on into its links.
   4. A SECOND TAP IS NOT A NEW INTENT. The fourth line now sits where the link
      was; a link in the merchant's prose landing under a fast second tap would
      navigate somewhere nobody asked to go. Clicks on links inside the copy are
      ignored for one unfold duration.

   One delegated listener, bound once, so a section re-render, the theme editor
   or a second description block needs nothing re-armed. Durations and easing
   are READ from snippets/design-tokens.liquid, never repeated here. */
(function () {
  'use strict';

  if (window.__ppDescriptionFold) return;
  window.__ppDescriptionFold = true;

  function token(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  function toMs(value, fallback) {
    var n = parseFloat(value);
    if (isNaN(n)) return fallback;
    return /ms$/.test(value) ? n : n * 1000;
  }

  function still() {
    if (window.PP && typeof window.PP.reducedMotion === 'function') return window.PP.reducedMotion();
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* --pp-dur-move for a short unfold, --pp-dur-settle for a long one, linear in
     between and capped at 900px of growth: a longer travel earns a little more
     time, never more than the theme's settle. */
  function duration(growth) {
    var short = toMs(token('--pp-dur-move'), 300);
    var long = toMs(token('--pp-dur-settle'), 440);
    return Math.round(short + (long - short) * Math.min(1, growth / 900));
  }

  var guardUntil = 0;

  document.addEventListener(
    'click',
    function (event) {
      if (performance.now() >= guardUntil) return;
      var a = event.target.closest ? event.target.closest('.product-description__body.is-open a[href]') : null;
      if (a) event.preventDefault();
    },
    true
  );

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button > 0) return;
    /* A modified click is a request for a second tab, which the fragment
       already answers in its own way. */
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest ? event.target.closest('[data-description-more]') : null;
    if (!link) return;
    var body = document.getElementById((link.getAttribute('href') || '').slice(1));
    if (!body) return;
    event.preventDefault();
    if (body.classList.contains('is-open')) return;

    /* THE LINK'S ROW IS HANDED TO THE COPY, NOT GIVEN BACK TO THE PAGE. The
       link retires the instant the copy opens, so the block after it would
       rise by the link's row for one frame before the growing copy pushed it
       down again. Measured with the first version of this file: an accordion
       summary passed under the shopper's finger at 390, 820 and 1440. So the
       unfold starts at the copy's height PLUS that row, read off the next
       block's own displacement (margins included, whatever they collapse
       into), and nothing below the copy ever moves up. */
    var from = body.getBoundingClientRect().height;
    var wrap = body.parentElement;
    var next = wrap ? wrap.nextElementSibling : null;
    var nextTop = next ? next.getBoundingClientRect().top : 0;
    body.classList.add('is-open');
    body.style.maxHeight = from + 'px';
    var start = from + (next ? Math.max(0, nextTop - next.getBoundingClientRect().top) : 0);
    body.style.maxHeight = '';
    var box = body.getBoundingClientRect();
    var growth = box.height - from;
    var reduce = still();
    var ms = duration(growth);
    guardUntil = performance.now() + ms;

    /* tabindex only now: in the markup it would make a plain click in the
       clipped text focus the box, and :focus-within would snap the fold open
       under the pointer with no animation. focus-shell drops the ring a
       programmatic focus would otherwise draw (assets/base.css). */
    body.setAttribute('tabindex', '-1');
    body.classList.add('focus-shell');
    try {
      body.focus({ preventScroll: true });
    } catch (error) {
      body.focus();
    }

    /* The one case that still scrolls: the first line already sits under the
       header (the shopper tapped the link with the copy above the fold). The
       full header height, not its painted edge, because an upward scroll is
       exactly what makes assets/header.js bring the collapsed row back. */
    var edge = Math.max(parseFloat(token('--pp-header-height')) || 0, parseFloat(token('--pp-header-bottom')) || 0) + 16;
    if (box.top < edge) window.scrollBy({ top: box.top - edge, behavior: reduce ? 'auto' : 'smooth' });

    if (reduce || typeof body.animate !== 'function' || growth < 1) return;
    body.classList.add('is-opening');
    var done = function () {
      body.classList.remove('is-opening');
    };
    var unfold = body.animate([{ maxHeight: Math.min(start, box.height) + 'px' }, { maxHeight: box.height + 'px' }], {
      duration: ms,
      easing: token('--pp-ease-settle') || 'ease-out'
    });
    unfold.onfinish = done;
    unfold.oncancel = done;
  });
})();
